import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError, takeUntil, tap} from 'rxjs/operators';
import {HTTP_STATUS, ResponseError} from '../../common/api-helpers';
import {dependOn} from '../../common/rxjs/depend-on';
import {destroy$} from '../../components/destroyable';
import {PARTIAL_LOADING, PartialLoading} from '../../components/partial-loading-indicator';
import {SignUpService} from '../../services/sign-up.service';

enum ResetPasswordPageState {
    FORM,
    SUCCESS
}

interface ResetPasswordFormControls {
    login: FormControl;
}

interface ResetPasswordForm extends FormGroup {
    controls: ResetPasswordFormControls & {[key: string]: AbstractControl};
}

interface ResetPasswordFormSuccessMessageSubstitutions {
    login: string;
}

interface ResetPasswordFormErrors {
    commonError?: string;
}

interface ResetPasswordFormFields {
    login: string;
}

const FORM_ERROR_MESSAGES = {
    loginIsRequired: 'components.reset-password-page.form_user_id_is_required_error',
    loginIsNotFound: 'components.reset-password-page.form_user_id_not_found_error'
};

@Component({
    selector: 'reset-password-page',
    templateUrl: './reset-password-page.component.html',
    styleUrls: ['./reset-password-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordPageComponent implements OnInit {

    @ViewChild('loadingIndicator', {read: PARTIAL_LOADING, static: true})
    public loadingIndicator: PartialLoading;

    public readonly ResetPasswordPageState = ResetPasswordPageState;

    public state: ResetPasswordPageState = ResetPasswordPageState.FORM;

    public form: ResetPasswordForm;

    public formErrors: ResetPasswordFormErrors;

    public successMessageSubstitutions: ResetPasswordFormSuccessMessageSubstitutions;

    public readonly destroy$ = destroy$();

    constructor(
        private signUpService: SignUpService
    ) {}

    public ngOnInit(): void {
        this.buildForm();
    }

    public submit(event: Event): void {
        event.preventDefault();

        this.formErrors = null;

        if (!this.loadingIndicator.getLoadingState() && this.form.valid) {
            this.sendResetPasswordRequest();
        } else {
            this.setFormErrors({
                commonError: FORM_ERROR_MESSAGES.loginIsRequired
            });
        }
    }

    private buildForm(): void {
        const controls: ResetPasswordFormControls = {
            login: new FormControl('', Validators.required)
        };

        this.form = new FormGroup(controls as ResetPasswordForm['controls']) as ResetPasswordForm;
    }

    private sendResetPasswordRequest(): void {
        const form: ResetPasswordFormFields = this.form.value;
        const login = form.login.trim();

        this.signUpService.requestPasswordReset(login).pipe(
            dependOn(this.loadingIndicator.loading$),
            tap(() => this.setSuccessState()),
            catchError(responseError => this.handlePasswordResetRequestError(responseError)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private setSuccessState(): void {
        this.state = ResetPasswordPageState.SUCCESS;
        this.successMessageSubstitutions = {
            login: this.form.controls.login.value
        };
    }

    private setFormErrors(errors: ResetPasswordFormErrors): void {
        this.formErrors = Object.assign(this.formErrors || {}, errors);
    }

    private handlePasswordResetRequestError(responseError: ResponseError): Observable<any> {
        let result$: Observable<any>;

        if (responseError.error.status === HTTP_STATUS.NOT_FOUND) {
            this.setFormErrors({
                commonError: FORM_ERROR_MESSAGES.loginIsNotFound
            });

            result$ = EMPTY;
        } else {
            result$ = throwError(() => responseError);
        }

        return result$;
    }

}
